import { toast } from '@postscript/components';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { QueryCacheTimes } from 'components/flowBuilder/constants';
import { api } from 'controllers/network/apiClient';
import { formatErrorMessage } from 'utils/errors';
import { useEffect, useRef } from 'react';
import {
  OfferCountsResponse,
  CreateOfferRequest,
  CreateOfferResponse,
  DeleteOfferParams,
  DeleteOfferResponse,
  Offer,
  OfferScanLifecycleStatus,
  OfferScanStatusResponse,
  OffersQueryParams,
  OffersResponse,
  TriggerOfferScanResponse,
  UpdateOfferParams,
  UpdateOfferResponse,
} from '../types';

export const ALL_OFFERS_QUERY_KEY = 'offers';
export const OFFERS_PATH = '/v2/ai-components/offers/';
export const OFFER_COUNTS_PATH = `${OFFERS_PATH}counts`;
export const OFFER_COUNTS_QUERY_KEY = 'counts';
export const OFFER_PROPOSALS_QUERY_KEY = 'offerProposals';
export const OFFER_PROPOSAL_SCAN_QUERY_KEY = 'offerProposalScan';

export async function getOffers(
  params?: OffersQueryParams,
): Promise<OffersResponse> {
  const searchParams = new URLSearchParams();

  if (params?.page) {
    searchParams.set('page', params.page.toString());
  }

  if (params?.isSitewideSale !== undefined) {
    searchParams.set('is_sitewide_sale__eq', params.isSitewideSale.toString());
  }

  const queryString = searchParams.toString();
  const url = queryString ? `${OFFERS_PATH}?${queryString}` : OFFERS_PATH;

  return api.get(url);
}

export const useOffers = (
  params?: OffersQueryParams,
): UseQueryResult<OffersResponse> => {
  return useQuery({
    queryKey: [ALL_OFFERS_QUERY_KEY, params],
    queryFn: () => getOffers(params),
    staleTime: QueryCacheTimes.MEDIUM,
    keepPreviousData: true,
  });
};

export async function getOfferById(id: number): Promise<Offer> {
  return api.get(`${OFFERS_PATH}${id}`);
}

export const useOfferById = (
  id: number | null | undefined,
): UseQueryResult<Offer> =>
  useQuery({
    queryKey: [ALL_OFFERS_QUERY_KEY, 'detail', id],
    queryFn: () => getOfferById(id as number),
    enabled: typeof id === 'number',
    staleTime: QueryCacheTimes.MEDIUM,
  });

export async function getOfferCounts(): Promise<OfferCountsResponse> {
  return api.get(OFFER_COUNTS_PATH);
}

export const useOfferCounts = (): UseQueryResult<OfferCountsResponse> => {
  return useQuery({
    queryKey: [ALL_OFFERS_QUERY_KEY, OFFER_COUNTS_QUERY_KEY],
    queryFn: getOfferCounts,
    staleTime: QueryCacheTimes.MEDIUM,
  });
};

export async function createOffer(
  offerData: CreateOfferRequest,
): Promise<CreateOfferResponse> {
  return api.post(OFFERS_PATH, offerData);
}

export const useCreateOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOffer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ALL_OFFERS_QUERY_KEY],
      });
      toast.success('Offer created successfully!');
    },
    onError: (error: unknown) => {
      console.error('Failed to create offer:', error);
      toast.error(
        formatErrorMessage(error, 'Failed to create offer. Please try again.'),
      );
    },
  });
};

export const OFFER_SCAN_STATUS_QUERY_KEY = 'offer-scan-status';
export const OFFER_SCAN_POLL_INTERVAL_MS = 2000;

export const getOfferScanRefetchInterval = (
  data?: OfferScanStatusResponse,
  query?: { state: { status: string } },
): false | number => {
  if (query?.state.status === 'error') return false;

  return data?.lifecycleStatus === OfferScanLifecycleStatus.RUNNING || !data
    ? OFFER_SCAN_POLL_INTERVAL_MS
    : false;
};

export async function triggerOfferScan(): Promise<TriggerOfferScanResponse> {
  return api.post(`${OFFERS_PATH}scan`, {});
}

export const useTriggerOfferScan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerOfferScan,
    onSuccess: (workflow) => {
      queryClient.invalidateQueries({
        queryKey: [OFFER_PROPOSALS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OFFER_PROPOSAL_SCAN_QUERY_KEY],
      });
      queryClient.resetQueries({
        queryKey: [
          OFFER_SCAN_STATUS_QUERY_KEY,
          workflow.workflowId,
          workflow.runId ?? null,
        ],
        exact: true,
      });
    },
    onError: () => {
      toast.error('Failed to start offer scan. Please try again.');
    },
  });
};

export async function getOfferScanStatus(
  workflowId: string,
  runId?: string | null,
): Promise<OfferScanStatusResponse> {
  const query = runId ? `?run_id=${encodeURIComponent(runId)}` : '';
  return api.get(
    `${OFFERS_PATH}scan/${encodeURIComponent(workflowId)}${query}`,
  );
}

export const useOfferScanStatus = (
  workflowId: string | null,
  runId?: string | null,
) => {
  const queryClient = useQueryClient();
  const invalidatedWorkflowRef = useRef<string | null>(null);
  const query = useQuery({
    queryKey: [OFFER_SCAN_STATUS_QUERY_KEY, workflowId, runId],
    queryFn: () => getOfferScanStatus(workflowId as string, runId),
    enabled: !!workflowId,
    refetchOnWindowFocus: false,
    refetchInterval: getOfferScanRefetchInterval,
  });

  useEffect(() => {
    const status = query.data?.lifecycleStatus;
    if (!workflowId || !status || status === OfferScanLifecycleStatus.RUNNING) {
      invalidatedWorkflowRef.current = null;
      return;
    }

    const workflowKey = `${workflowId}:${runId ?? ''}`;
    if (invalidatedWorkflowRef.current === workflowKey) return;

    invalidatedWorkflowRef.current = workflowKey;
    queryClient.invalidateQueries({ queryKey: [OFFER_PROPOSALS_QUERY_KEY] });
  }, [query.data?.lifecycleStatus, queryClient, runId, workflowId]);

  return query;
};

export async function updateOffer({
  id,
  data,
}: UpdateOfferParams): Promise<UpdateOfferResponse> {
  return api.patch(`${OFFERS_PATH}${id}`, data);
}

export const useUpdateOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOffer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ALL_OFFERS_QUERY_KEY],
      });
      toast.success('Offer updated successfully!');
    },
    onError: (error: unknown) => {
      console.error('Failed to update offer:', error);
      toast.error(
        formatErrorMessage(error, 'Failed to update offer. Please try again.'),
      );
    },
  });
};

export async function deleteOffer({
  id,
}: DeleteOfferParams): Promise<DeleteOfferResponse> {
  return api.delete(`${OFFERS_PATH}${id}`);
}

export const useDeleteOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteOffer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ALL_OFFERS_QUERY_KEY],
      });
      toast.success('Offer deleted successfully!');
    },
    onError: (error: unknown) => {
      console.error('Failed to delete offer:', error);
      toast.error(
        formatErrorMessage(error, 'Failed to delete offer. Please try again.'),
      );
    },
  });
};
